import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Check, ArrowRight, Shield, Heart, Clock, Mic2 } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PreorderPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [preorderCount, setPreorderCount] = useState(0);

  useEffect(() => {
    fetch(`${BACKEND}/api/preorder/stats`)
      .then((r) => r.json())
      .then((d) => setPreorderCount(d.paid || 0))
      .catch(() => {});
  }, []);

  const handlePreorder = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Inserisci nome e email");
      return;
    }
    if (!email.includes("@")) {
      toast.error("Email non valida");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/preorder/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          origin_url: window.location.origin,
          source: new URLSearchParams(window.location.search).get("src") || "direct",
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Errore nel checkout");
      }
    } catch {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(30_25%_98%)]" data-testid="preorder-page">
      {/* Minimal header */}
      <header className="py-6 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
          <span className="font-serif text-xl font-bold text-foreground">Io</span>
          <span className="font-serif text-xl font-bold text-accent">Ci</span>
          <span className="font-serif text-xl font-bold text-foreground">Sono</span>
          <span className="text-[10px] text-muted-foreground font-sans ml-0.5">.org</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="text-center mt-8 mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-4">Founder Access</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-5">
            Le persone che ami<br />
            <span className="text-accent">non devono scomparire.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
            IoCiSono preserva la voce, la personalita e i ricordi di chi non c'e piu.
            Per parlare ancora con loro. Per sempre.
          </p>
        </motion.div>

        {/* Video */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="mb-12">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-elevated bg-[hsl(220_25%_10%)]"
            data-testid="video-section">
            <video
              className="w-full h-full object-cover"
              controls
              playsInline
              preload="metadata"
              poster=""
              data-testid="demo-video"
            >
              <source src="/iocisono-demo.mp4" type="video/mp4" />
            </video>
          </div>
        </motion.div>

        {/* 3 Bullets */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}
          className="grid sm:grid-cols-3 gap-4 mb-12">
          {[
            {
              icon: Heart,
              color: "text-rose-400",
              bg: "bg-rose-400/8",
              title: "1 profilo completo",
              desc: "Crea l'identita digitale di una persona cara con personalita, voce e ricordi.",
            },
            {
              icon: Mic2,
              color: "text-accent",
              bg: "bg-accent/8",
              title: "60 min di voce",
              desc: "Registra o carica messaggi vocali per clonare la voce in modo realistico.",
            },
            {
              icon: Clock,
              color: "text-emerald-500",
              bg: "bg-emerald-500/8",
              title: "Sconto lifetime 50%",
              desc: "I Founder avranno per sempre il 50% di sconto su ogni upgrade futuro.",
            },
          ].map((item, i) => (
            <Card key={i} className="bg-card border-border/20 shadow-soft">
              <CardContent className="p-5 text-center">
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center mx-auto mb-3`}>
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                </div>
                <h3 className="font-serif text-sm font-bold text-foreground mb-1.5">{item.title}</h3>
                <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </motion.div>

        {/* Pre-order card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}>
          <Card className="bg-card border-border/30 shadow-elevated max-w-md mx-auto" data-testid="preorder-card">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <p className="text-xs uppercase tracking-[0.15em] text-accent font-medium mb-2">Founder Access</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-serif text-4xl font-bold text-foreground">29</span>
                  <span className="text-lg text-muted-foreground">€</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">una tantum · accesso anticipato</p>
              </div>

              <div className="space-y-2 mb-6">
                {[
                  "Accesso anticipato alla piattaforma",
                  "1 profilo completo con AI conversazionale",
                  "60 minuti di registrazione voce",
                  "Sconto lifetime 50% su tutti gli upgrade",
                  "Conservazione dati garantita",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-5">
                <Input placeholder="Il tuo nome" value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="preorder-name" />
                <Input type="email" placeholder="La tua email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="preorder-email" />
              </div>

              <Button variant="premium" size="lg" className="w-full" onClick={handlePreorder}
                disabled={loading} data-testid="preorder-btn">
                {loading ? "Reindirizzamento al pagamento..." : (
                  <>Pre-ordina ora · 29€ <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>

              <div className="flex items-center justify-center gap-4 mt-4">
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Shield className="w-3 h-3" /> Pagamento sicuro
                </span>
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Check className="w-3 h-3" /> Stripe
                </span>
              </div>

              {preorderCount > 0 && (
                <p className="text-center text-[11px] text-muted-foreground mt-4" data-testid="preorder-count">
                  {preorderCount} {preorderCount === 1 ? "persona ha" : "persone hanno"} gia pre-ordinato
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Trust */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-center mt-10">
          <p className="text-xs text-muted-foreground/50 max-w-sm mx-auto">
            IoCiSono.org e un servizio di conservazione emotiva. Non un social network.
            I tuoi ricordi meritano di essere custoditi per sempre.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
